import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Loader2, ReceiptEuro, Undo2 } from 'lucide-react';
import { fetchMyRMAOrdine } from '../../services/rmaService';
import { LoadError } from '../../components/rma/LoadError';
import { hrefDocumento } from '../../components/rma/documentiRma';
import type { OrdineRMA, StatoOrdineRMA } from '../../types';

/** L'Ordine RMA di una pratica chiusa (INT-133): il reso registrato sul
 *  gestionale e a che punto è il rimborso. Non è tra gli ordini del Cliente. */

const fmtEur = (n: number) => n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtData = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');
const fmtQta = (n: number) => n.toLocaleString('it-IT', { maximumFractionDigits: 2 });

const STATO: Record<StatoOrdineRMA, { label: string; pill: string }> = {
  da_accreditare:       { label: 'Da accreditare',       pill: 'bg-amber-50 text-amber-800 border-amber-200' },
  accreditato_in_parte: { label: 'Accreditato in parte', pill: 'bg-sky-50 text-sky-800 border-sky-200' },
  accreditato:          { label: 'Accreditato',          pill: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  annullato:            { label: 'Annullato',            pill: 'bg-secondary text-muted-foreground border-border' },
};

const STATO_RIGA: Record<OrdineRMA['righe'][number]['stato'], { label: string; cls: string }> = {
  aperta:    { label: 'Da accreditare', cls: 'text-amber-700' },
  parziale:  { label: 'In parte',       cls: 'text-sky-700' },
  evasa:     { label: 'Accreditato',    cls: 'text-emerald-700' },
  chiusa:    { label: 'Chiuso',         cls: 'text-muted-foreground' },
  annullata: { label: 'Annullato',      cls: 'text-muted-foreground line-through' },
};

/** Tre passi: reso approvato, ordine di reso, nota di credito. */
function Avanzamento({ stato }: { stato: StatoOrdineRMA }) {
  const passi = ['Reso approvato', 'Ordine di reso', stato === 'accreditato_in_parte' ? 'Accreditato in parte' : 'Accreditato'];
  const fatti = stato === 'da_accreditare' ? 2 : 3;
  return (
    <ol className="flex items-start" data-testid="rma-ordine-avanzamento">
      {passi.map((p, i) => {
        const fatto = i < fatti;
        return (
          <li key={p} className="flex-1 flex flex-col items-center relative">
            {i > 0 && (
              <span className={`absolute right-1/2 top-3 w-full h-0.5 -z-0 ${fatto ? 'bg-[hsl(210,64%,29%)]' : 'bg-border'}`} />
            )}
            <span className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center border-2 ${
              fatto ? 'bg-[hsl(210,64%,29%)] border-[hsl(210,64%,29%)] text-white' : 'bg-white border-border'}`}>
              {fatto && <Check className="w-3.5 h-3.5" />}
            </span>
            <span className={`mt-1.5 text-[11px] text-center font-medium ${fatto ? 'text-foreground' : 'text-muted-foreground'}`}>{p}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function RMAOrderPage() {
  const { rmaId } = useParams<{ rmaId: string }>();
  const [ordine, setOrdine] = useState<OrdineRMA | null>(null);
  const [esito, setEsito] = useState<'caricamento' | 'ok' | 'assente' | 'errore'>('caricamento');
  const [tentativo, setTentativo] = useState(0);

  useEffect(() => {
    if (!rmaId) return;
    let annullato = false;
    setEsito('caricamento');
    fetchMyRMAOrdine(rmaId)
      .then(o => { if (!annullato) { setOrdine(o); setEsito('ok'); } })
      .catch(e => { if (!annullato) setEsito(e?.response?.status === 404 ? 'assente' : 'errore'); });
    return () => { annullato = true; };
  }, [rmaId, tentativo]);

  const indietro = (
    <Link to={`/area-clienti/rma/${rmaId}`} data-testid="rma-ordine-back"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
      <ChevronLeft className="w-4 h-4" /> {rmaId}
    </Link>
  );

  if (esito === 'caricamento') {
    return <>{indietro}<div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…</div></>;
  }
  if (esito === 'errore') {
    return <>{indietro}<LoadError message="Non è stato possibile caricare l'ordine di reso." onRetry={() => setTentativo(t => t + 1)} testId="rma-ordine-error" /></>;
  }
  if (esito === 'assente' || !ordine) {
    return <>{indietro}<div className="rounded-xl border bg-white p-8 text-center text-sm text-muted-foreground" data-testid="rma-ordine-assente">Ordine di reso non disponibile.</div></>;
  }

  const st = STATO[ordine.stato];
  return (
    <div data-testid="rma-ordine-page" className="space-y-4">
      {indietro}

      <div className="rounded-xl border bg-white p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-[hsl(210,64%,29%)]/10 text-[hsl(210,64%,29%)] flex items-center justify-center flex-shrink-0">
            <Undo2 className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Ordine di reso</p>
            <h1 className="font-mono text-lg sm:text-xl font-bold leading-tight break-all" data-testid="rma-ordine-numero">{ordine.numero}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{fmtData(ordine.data)} · pratica {ordine.rma}</p>
          </div>
          {/* Su mobile sotto il numero, allineato al testo. */}
          <div className="basis-full sm:basis-auto pl-14 sm:pl-0">
            <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full border ${st.pill}`} data-testid="rma-ordine-stato">{st.label}</span>
          </div>
        </div>
        {ordine.stato !== 'annullato' && <div className="mt-5"><Avanzamento stato={ordine.stato} /></div>}
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <ul className="divide-y">
          {ordine.righe.map((r, i) => {
            const sr = STATO_RIGA[r.stato];
            return (
              <li key={i} className="px-4 py-3 flex items-center gap-3" data-testid={`rma-ordine-riga-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.title}</p>
                  <p className="text-[11px] text-muted-foreground">
                    <span className="font-mono">{r.sku}</span> · {fmtQta(r.quantita)} pz
                    {r.stato === 'parziale' && <> · {fmtQta(r.quantitaAccreditata)} {r.quantitaAccreditata === 1 ? 'accreditato' : 'accreditati'}</>}
                  </p>
                </div>
                <span className={`text-[11px] font-semibold whitespace-nowrap ${sr.cls}`}>{sr.label}</span>
                <span className="w-20 text-right font-mono text-sm tabular-nums">{fmtEur(r.totaleRiga)}</span>
              </li>
            );
          })}
        </ul>
        <div className="px-4 py-3 border-t bg-[hsl(210,40%,98%)] flex items-baseline justify-end gap-4">
          {ordine.totale != null && (
            <span className="text-xs text-muted-foreground tabular-nums">{fmtEur(ordine.totale)} IVA esclusa</span>
          )}
          <span className="text-sm font-semibold">Totale reso</span>
          <span className="font-mono text-lg font-bold tabular-nums" data-testid="rma-ordine-totale">{fmtEur(ordine.totaleIvato)}</span>
        </div>
      </div>

      {ordine.noteCredito.map(n => (
        <Link key={`${n.anno}-${n.serie}-${n.numdoc}`} to={hrefDocumento(n)} data-testid="rma-ordine-nota-credito"
          className="rounded-xl border bg-white px-4 py-3 flex items-center gap-3 hover:border-[hsl(var(--dtk-focus))] transition-colors">
          <ReceiptEuro className="w-5 h-5 text-emerald-700" />
          <span className="text-sm font-medium flex-1">Nota di credito <span className="font-mono">{n.numero}</span></span>
          {n.totale != null && <span className="font-mono text-sm tabular-nums">{fmtEur(n.totale)}</span>}
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </Link>
      ))}
    </div>
  );
}
